/**
 * Responsive layout for the bulk-selection bar: given the actions a page offers and the width
 * the bar can use, decide which actions show their label, which shrink to an icon, and which
 * move into the "More" menu. Pure so the rules are unit-testable without a DOM.
 *
 * Widths are estimates (sm buttons, ~7px per character), not measurements: measuring each control
 * would make the layout depend on itself. They only need to be close enough to pick a collapse step.
 */

export type BarDisplay = 'label' | 'icon' | 'more';

export type BarActionSpec = {
  id: string;
  /** Primary actions stay visible longest; the first primary in `priority` order never leaves the bar. */
  primary: boolean;
  /** Lower = more important. Ties keep their given order. */
  priority: number;
  /** Plain buttons can move into the More menu; selects can only shrink to their icon. */
  overflowable: boolean;
  /** Estimated width with its label. Equal to `iconWidth` for controls that are always icon-only. */
  labeledWidth: number;
  /** Estimated width when shrunk to its icon. */
  iconWidth: number;
};

export type BarLayout = {
  display: Record<string, BarDisplay>;
  showMore: boolean;
};

/** The ✕, the "N selected" count, the bar's padding. */
export const BAR_CHROME_WIDTH = 150;
/** Space between controls (`gap-3`). */
export const BAR_GAP = 12;
/** The "More" trigger, icon + chevron. */
export const MORE_WIDTH = 52;
/** Horizontal breathing room kept between the bar and the content area's edges. */
export const BAR_SIDE_MARGIN = 32;

const CHAR_WIDTH = 7;
const BUTTON_PADDING_AND_ICON = 42;
export const ICON_BUTTON_WIDTH = 32;

/** Estimated width of an sm button with an icon and this label. */
export function estimateLabeledWidth(label: string): number {
  return BUTTON_PADDING_AND_ICON + Math.ceil(label.length * CHAR_WIDTH);
}

function totalWidth(actions: BarActionSpec[], display: Record<string, BarDisplay>): number {
  const visible = actions.filter((a) => display[a.id] !== 'more');
  const showMore = visible.length < actions.length;
  const controls = visible.reduce((sum, a) => sum + (display[a.id] === 'label' ? a.labeledWidth : a.iconWidth), 0);
  const count = visible.length + (showMore ? 1 : 0);
  return BAR_CHROME_WIDTH + controls + (showMore ? MORE_WIDTH : 0) + Math.max(0, count) * BAR_GAP;
}

/**
 * Collapse order, applied one action at a time until the bar fits:
 * 1. secondary labels → icons (least important first)
 * 2. secondary buttons → More (least important first)
 * 3. primary labels → icons, except the most important primary
 * 4. primary buttons → More, except the most important primary
 * 5. the most important primary's label → icon
 * If it still does not fit, the result is the most collapsed layout (nothing is ever dropped).
 */
export function computeSelectionBarLayout(actions: BarActionSpec[], availableWidth: number): BarLayout {
  const display: Record<string, BarDisplay> = {};
  for (const a of actions) display[a.id] = 'label';

  const byImportance = [...actions].sort((a, b) => a.priority - b.priority);
  const leastFirst = [...byImportance].reverse();
  const topPrimary = byImportance.find((a) => a.primary);

  const steps: Array<() => BarActionSpec[]> = [
    () => leastFirst.filter((a) => !a.primary && a.labeledWidth > a.iconWidth),
    () => leastFirst.filter((a) => !a.primary && a.overflowable),
    () => leastFirst.filter((a) => a.primary && a !== topPrimary && a.labeledWidth > a.iconWidth),
    () => leastFirst.filter((a) => a.primary && a !== topPrimary && a.overflowable),
    () => (topPrimary != null && topPrimary.labeledWidth > topPrimary.iconWidth ? [topPrimary] : []),
  ];
  const targets: BarDisplay[] = ['icon', 'more', 'icon', 'more', 'icon'];

  const fits = () => totalWidth(actions, display) <= availableWidth;
  for (let s = 0; s < steps.length && !fits(); s++) {
    const target = targets[s] ?? 'icon';
    for (const a of steps[s]?.() ?? []) {
      if (fits()) break;
      display[a.id] = target;
    }
  }

  return { display, showMore: actions.some((a) => display[a.id] === 'more') };
}
